import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NOTIFICATION_SECTIONS, NOTIFICATION_TYPES, type NotificationPrefsPatch, type NotificationSection, type PushDevice } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { currentEndpoint, disablePush, enablePush, pushState, type PushState } from "../lib/push";
import { Group, Saved } from "../pages/club/ClubSettingsPage";
import { Sheet, Spinner } from "./ui";
import { AlertIcon, LockIcon, MailIcon, MoonIcon, PhoneIcon } from "./Icons";
import { AddressSheet, useMyEmails } from "./Emails";
import { notifications } from "@pagemates/shared/copy";

const txt = notifications.settings;

/*
 * Settings → Notifications (PLAN.md §Notifications, and §Email addresses for the
 * channels; mock artifact 6Pet7JpXvLjCqv3zkhFP29): the channels first (this device's
 * push and the other devices, where emails go, quiet hours), then a table with a row
 * per kind of notification and a column per channel. The inbox gets everything and
 * has no column. Taps save at once and say so beside the name.
 */

const ABOUT: Record<NotificationSection, string> = {
  you: txt.thingsMeantForYou,
  meetings: txt.yourClubsMeetings,
  picking: txt.whatYourClubsRead,
  running: txt.forOwnersAndAdmins,
  activity: txt.whatSHappeningIn,
};

const DEVICE: Record<PushState, string> = {
  on: txt.thisPhoneOrBrowser,
  off: txt.thisPhoneOrBrowser2,
  denied: txt.notificationsAreBlockedFor,
  "ios-install": txt.onIphoneAndIpad,
  unsupported: txt.thisBrowserCanT,
  unavailable: txt.thisServerDoesnT,
};

const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const minutes = (v: string) => { const [h, m] = v.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };

export function NotificationSettings() {
  const qc = useQueryClient();
  const prefs = useQuery({ queryKey: ["notif-prefs"], queryFn: api.notificationPrefs });
  const [saved, setSaved] = useState<string | null>(null);
  const [sheet, setSheet] = useState<"address" | "devices" | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const flash = (k: string) => { setSaved(k); clearTimeout(timer.current); timer.current = setTimeout(() => setSaved(null), 1800); };
  const save = useMutation({ mutationFn: api.updateNotificationPrefs, onSuccess: (p) => qc.setQueryData(["notif-prefs"], p) });
  const put = (key: string, patch: NotificationPrefsPatch) => save.mutate(patch, { onSuccess: () => flash(key) });
  const emails = useMyEmails(Boolean(prefs.data?.emailAvailable));

  const [device, setDevice] = useState<PushState | null>(null);
  const [here, setHere] = useState<string | null>(null);
  const [deviceBusy, setDeviceBusy] = useState(false);
  useEffect(() => { void pushState().then(setDevice); void currentEndpoint().then(setHere); }, []);
  const toggleDevice = (on: boolean) => {
    setDeviceBusy(true);
    void (on ? enablePush() : disablePush()).then((s) => {
      setDevice(s); if (s === (on ? "on" : "off")) flash(txt.device);
      void currentEndpoint().then(setHere); void qc.invalidateQueries({ queryKey: ["notif-prefs"] });
    }).finally(() => setDeviceBusy(false));
  };

  if (prefs.isPending) return <Group title={txt.notifications} about={txt.whatReachesYouAnd}><Spinner /></Group>;
  if (!prefs.data) return <Group title={txt.notifications} about={txt.whatReachesYouAnd}><p className="error">{describeError(prefs.error)}</p></Group>;
  const p = prefs.data;
  const quiet = p.quietStart !== null && p.quietEnd !== null;
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tick = (k: string) => <Saved on={saved === k} />;
  const others = p.devices.filter((d) => d.endpoint !== here);
  const thisDevice = p.devices.find((d) => d.endpoint === here);
  const pending = emails.data?.pending ?? null;
  // The email column works only with somewhere to send and the email switch on.
  const emailOn = p.emailAvailable && Boolean(p.notifyEmail) && p.email;
  const byKey = new Map(p.types.map((t) => [t.key, t]));

  return (
    <>
      <Group title={txt.notifications} about={txt.yourInboxGetsEverything}>
        <div className="chan" id="notifications">
          <span className="chan-ic"><PhoneIcon width={18} height={18} /></span>
          <div className="chan-main"><div className="k">{txt.pushOnThisDevice}{tick("device")}</div>
            <div className="d">{device === "on" || device === "off" ? thisDevice?.label ?? txt.thisBrowser : device ? DEVICE[device] : txt.checking}</div></div>
          {(device === "on" || device === "off") && (
            <input className="toggle" type="checkbox" checked={device === "on"} disabled={deviceBusy} aria-label={txt.pushOnThisDevice} onChange={(e) => toggleDevice(e.target.checked)} />
          )}
        </div>
        {others.length > 0 && <button type="button" className="linkish small chan-more" onClick={() => setSheet("devices")}>{txt.otherDevices(others.length)}</button>}

        {p.emailAvailable && <>
          <hr className="divider" />
          <div className="chan">
            <span className="chan-ic"><MailIcon width={18} height={18} /></span>
            <div className="chan-main">
              <div className="k">{txt.sendEmailsTo}{tick("email")}</div>
              {p.notifyEmail ? <>
                <div className="addr">{p.notifyEmail.address}</div>
                <span className="chips"><span className="chip-s ok">{txt.confirmed}</span>{p.notifyEmail.isRelay && <span className="chip-s">{txt.hiddenByApple}</span>}</span>
              </> : <div className="d">{p.emailUndeliverable ? txt.nowhereRightNow : txt.noAddressYet}</div>}
            </div>
            {p.notifyEmail && <button type="button" className="btn sm" onClick={() => setSheet("address")}>{txt.change}</button>}
          </div>
          {pending && (
            <div className="chan-note">
              <div className="addr">{pending.address}</div>
              <span className="chip-s">{txt.waitingForYouTo}</span>
              <div className="d">{txt.weSentItA(p.notifyEmail ? txt.emailsKeepGoingTo(p.notifyEmail.address) : "")}</div>
              <button type="button" className="linkish small" onClick={() => setSheet("address")}>{txt.enterTheCode}</button>
            </div>
          )}
          {p.notifyEmail?.isRelay && <p className="d" style={{ margin: 0 }}>{txt.appleForwardsTheseTo}</p>}
          {p.emailUndeliverable && (
            <div className="warn-note"><AlertIcon width={16} height={16} /><span>{txt.weCouldnTDeliver}</span>
              <button type="button" className="btn primary sm" onClick={() => setSheet("address")}>{txt.chooseAnotherAddress}</button></div>
          )}
          {!p.notifyEmail && !p.emailUndeliverable && !pending && <button type="button" className="btn primary sm" style={{ alignSelf: "flex-start" }} onClick={() => setSheet("address")}>{txt.addAnAddress}</button>}
          {!p.email && (
            <div className="warn-note">
              <AlertIcon width={16} height={16} />
              <span>{p.emailComplained ? txt.oneOfOurEmails : txt.emailIsOffYou}</span>
              <label className="chan" style={{ padding: 0 }}><span className="k" style={{ flex: 1 }}>{txt.email}</span>
                <input className="toggle" type="checkbox" checked={false} aria-label={txt.turnEmailBackOn} onChange={() => put("email", { email: true })} /></label>
            </div>
          )}
        </>}

        <hr className="divider" />
        <div className="chan">
          <span className="chan-ic"><MoonIcon width={18} height={18} /></span>
          <div className="chan-main"><div className="k">{txt.quietHours}{tick("quiet")}</div><div className="d">{txt.pushesWaitThenArrive}</div></div>
          <input className="toggle" type="checkbox" checked={quiet} aria-label={txt.quietHours}
            onChange={(e) => put("quiet", e.target.checked ? { quietStart: 22 * 60, quietEnd: 8 * 60 } : { quietStart: null, quietEnd: null })} />
        </div>
        {quiet && (
          <div className="sub-settings quiet-hours">
            <label>{txt.from}<input className="input" type="time" value={hhmm(p.quietStart!)} onChange={(e) => e.target.value && put("quiet", { quietStart: minutes(e.target.value), quietEnd: p.quietEnd })} /></label>
            <label>{txt.until}<input className="input" type="time" value={hhmm(p.quietEnd!)} onChange={(e) => e.target.value && put("quiet", { quietStart: p.quietStart, quietEnd: minutes(e.target.value) })} /></label>
          </div>
        )}
        <p className="muted small" style={{ margin: 0 }}>{txt.timesAre}{p.timeZone ? <>in <b>{p.timeZone.replace(/_/g, " ")}</b></> : txt.inYourClubOwner}.{tick("tz")}
          {zone && zone !== p.timeZone && <> <button type="button" className="linkish" onClick={() => put("tz", { timeZone: zone })}>{txt.use(zone.replace(/_/g, " "))}</button></>}
        </p>
        {save.isError && <p className="error small">{describeError(save.error)}</p>}
      </Group>

      <section className="sgroup ntab-group">
        <header><h2>{txt.whatReachesYou}</h2><p>{txt.yourInboxGetsEverything2(!emailOn && p.emailAvailable ? txt.addAnEmailAddress : "")}</p></header>
        <div className={`card ntab${p.emailAvailable ? "" : " no-email"}`}>
          <div className="ntab-head" aria-hidden="true"><span>{txt.kindOfNotification}</span><span>{txt.push}</span>{p.emailAvailable && <span>{txt.email}</span>}</div>
          {NOTIFICATION_SECTIONS.map((sec) => {
            const rows = NOTIFICATION_TYPES.filter((t) => t.section === sec.key && byKey.has(t.key));
            if (rows.length === 0) return null;
            return (
              <div key={sec.key} className="ntab-sec">
                <div className="ntab-sec-h">{sec.label}<span>{ABOUT[sec.key]}</span></div>
                {rows.map((t) => {
                  const c = byKey.get(t.key)!;
                  return (
                    <div key={t.key} className="ntab-row">
                      <div className="ntab-kind"><div className="k">{t.label}{tick(t.key)}</div><div className="d">{t.about}</div></div>
                      {c.push === null && c.email === null ? <div className="ntab-inbox">{txt.inboxOnly}</div> : <>
                        <Cell label={txt.push} kind={t.label} value={c.push} locked={c.locked} onChange={(v) => put(t.key, { types: { [t.key]: { push: v } } })} />
                        {p.emailAvailable && <Cell label={txt.email} kind={t.label} value={c.email} locked={c.locked} disabled={!emailOn}
                          onChange={(v) => put(t.key, { types: { [t.key]: { email: v } } })} />}
                      </>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        {p.mutedClubs.length > 0 && <p className="muted small" style={{ margin: 0 }}>{txt.changeThatInEach(p.mutedClubs.length === 1 ? txt.n1ClubMuted : txt.clubsMuted(p.mutedClubs.length))}</p>}
      </section>

      {sheet === "address" && <AddressSheet onClose={() => setSheet(null)} />}
      {sheet === "devices" && <DevicesSheet devices={p.devices} here={here} onClose={() => setSheet(null)} />}
    </>
  );
}

/** One switch in the table: labelled Push or Email beside it on phones; locked ones say "On". */
function Cell({ label, kind, value, locked, disabled, onChange }: { label: string; kind: string; value: boolean | null; locked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) {
  if (value === null) return <div className="ntab-cell"><span className="ntab-cell-l">{label}</span><span className="muted" aria-label={txt.no(label.toLowerCase())}>–</span></div>;
  if (locked) return <div className="ntab-cell"><span className="ntab-cell-l">{label}</span><span className="ntab-lock" title={txt.alwaysSentBecauseIt}><LockIcon width={12} height={12} />{txt.on}</span></div>;
  return (
    <label className="ntab-cell"><span className="ntab-cell-l">{label}</span>
      <input className="toggle" type="checkbox" checked={value && !disabled} disabled={disabled} aria-label={`${label}: ${kind}`} onChange={(e) => onChange(e.target.checked)} /></label>
  );
}

/** "Other devices": each one that gets push, with Remove. */
function DevicesSheet({ devices, here, onClose }: { devices: PushDevice[]; here: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const remove = useMutation({ mutationFn: api.removeDevice, onSuccess: () => qc.invalidateQueries({ queryKey: ["notif-prefs"] }) });
  return (
    <Sheet label={txt.devicesThatGetPush} onClose={onClose}>
      <div className="prows">
        {devices.map((d) => (
          <div key={d.id} className="prow">
            <span className="pi"><PhoneIcon width={16} height={16} /></span>
            <div className="t">{d.label}<small>{d.endpoint === here ? txt.thisDevice : d.lastSuccessAt ? txt.lastReached(new Date(d.lastSuccessAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })) : txt.notReachedYet}</small></div>
            {d.endpoint !== here && <button type="button" className="linkish small muted" disabled={remove.isPending} onClick={() => remove.mutate(d.id)}>{txt.remove}</button>}
          </div>
        ))}
      </div>
      <p className="small muted" style={{ margin: 0 }}>{txt.aRemovedDeviceStops}</p>
      {remove.isError && <p className="error small">{describeError(remove.error)}</p>}
    </Sheet>
  );
}

/** "Mute this club", in the club's own settings. Everything stops except someone asking to take the club over. */
export function MuteClub({ clubId }: { clubId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["club-mute", clubId], queryFn: () => api.clubMute(clubId) });
  const [saved, setSaved] = useState(false);
  const set = useMutation({
    mutationFn: (muted: boolean) => api.setClubMute(clubId, muted),
    onSuccess: (r) => { qc.setQueryData(["club-mute", clubId], r); void qc.invalidateQueries({ queryKey: ["notif-prefs"] }); setSaved(true); window.setTimeout(() => setSaved(false), 1800); },
  });
  return (
    <div className="switch">
      <div><div className="k">{txt.muteThisClub}<Saved on={saved} /></div><div className="d">{txt.nothingFromThisClub}</div></div>
      <input className="toggle" type="checkbox" checked={q.data?.muted ?? false} disabled={q.isPending || set.isPending} aria-label={txt.muteThisClub}
        onChange={(e) => set.mutate(e.target.checked)} />
    </div>
  );
}
